import { cn } from "design-system/utils"
import { FileText, Music } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CustomerIssueResponseDataSchema, MessageFormatEnum } from "schema"
import { z } from "zod"

type IssueMediaItems = z.infer<typeof CustomerIssueResponseDataSchema>["media"]

type Props = {
  media: IssueMediaItems
}

const MEDIA_ITEM_CLASS_NAME = "size-40 overflow-hidden rounded-md border border-border object-cover"

export function IssueMedia({ media }: Props) {
  const { t } = useTranslation()

  if (media.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-medium text-muted-foreground">
        {t("support.entities.issueMedia")}
      </span>
      <div className="flex flex-wrap gap-2">
        {media.map((item) => (
          <IssueMediaItem key={item.messageId} item={item} />
        ))}
      </div>
    </div>
  )
}

function IssueMediaItem({ item }: { item: IssueMediaItems[number] }) {
  const { t } = useTranslation()

  switch (item.format) {
    case MessageFormatEnum.Image:
      return (
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          <img
            src={item.url}
            alt={t("support.entities.issueMedia")}
            className={MEDIA_ITEM_CLASS_NAME}
          />
        </a>
      )
    case MessageFormatEnum.Video:
      return <video src={item.url} className={MEDIA_ITEM_CLASS_NAME} />
    case MessageFormatEnum.Audio:
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("support.entities.issueMedia")}
          className={cn(
            MEDIA_ITEM_CLASS_NAME,
            "flex items-center justify-center text-muted-foreground",
          )}
        >
          <Music className="size-6" />
        </a>
      )
    case MessageFormatEnum.Document:
    case MessageFormatEnum.Text:
    case MessageFormatEnum.Unsupported:
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("support.entities.issueAttachment")}
          className={cn(
            MEDIA_ITEM_CLASS_NAME,
            "flex items-center justify-center text-muted-foreground",
          )}
        >
          <FileText className="size-6" />
        </a>
      )
  }
}
